import { cn } from '@lqa-frontend/ui-system';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import { Icon } from '../icon';
import type { IconSize } from '../icon';

const buttonVariants = cva(
	[
		'cursor-pointer inline-flex items-center justify-center',
		'whitespace-nowrap font-semibold',
		'transition-[color,background-color,border-color,box-shadow]',
		'disabled:pointer-events-none disabled:cursor-not-allowed',
		'[&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0',
		'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-outline-interactive',
	].join(' '),
	{
		variants: {
			variant: {
				// ── Primary types ────────────────────────────────────────────
				default:
					'bg-interactive border border-interactive text-text-white shadow-sm hover:bg-brand-blue-500 hover:border-brand-blue-500 active:bg-brand-blue-700 active:border-brand-blue-700 disabled:bg-zinc-300 disabled:text-text-secondary disabled:border-zinc-300',
				warning:
					'bg-warning border border-warning text-text-white shadow-sm hover:bg-orange-500 hover:border-orange-500 active:bg-orange-700 active:border-orange-700 disabled:bg-zinc-300 disabled:text-text-secondary disabled:border-zinc-300',
				danger:
					'bg-danger border border-danger text-text-white shadow-sm hover:bg-red-500 hover:border-red-500 active:bg-red-700 active:border-red-700 disabled:bg-zinc-300 disabled:text-text-secondary disabled:border-zinc-300',
				success:
					'bg-success border border-success text-text-white shadow-sm hover:bg-green-400 hover:border-green-400 active:bg-green-600 active:border-green-600 disabled:bg-zinc-300 disabled:text-text-secondary disabled:border-zinc-300',
				ai: 'bg-fuchsia-600 border border-fuchsia-600 text-text-white shadow-sm hover:bg-fuchsia-400 hover:border-fuchsia-400 active:bg-fuchsia-700 active:border-fuchsia-700 disabled:bg-zinc-300 disabled:text-text-secondary disabled:border-zinc-300',

				// ── Secondary / outline types ────────────────────────────────
				outline:
					'bg-base-1 border border-outline-neutral text-text-primary shadow-sm hover:bg-base-2 active:bg-base-2 active:border-divider-bold disabled:bg-base-1 disabled:text-zinc-400 disabled:border-outline-neutral',
				filters:
					'bg-base-1 border border-outline-neutral text-text-primary shadow-sm hover:bg-base-2 active:bg-base-2 active:border-divider-bold disabled:bg-base-1 disabled:text-zinc-400 disabled:border-outline-neutral',
				dangerOutline:
					'bg-base-1 border border-outline-danger text-text-danger shadow-sm hover:bg-danger-light active:bg-outline-danger disabled:bg-base-1 disabled:text-zinc-400 disabled:border-outline-neutral',

				// ── Ghost (no border, interactive color) ─────────────────────
				ghost:
					'bg-transparent border border-transparent text-text-link hover:bg-interactive-light hover:shadow-sm active:bg-brand-blue-200 disabled:text-zinc-400',

				// ── Light theme (for dark backgrounds) ──────────────────────
				lightPrimary:
					'bg-white border border-white text-text-primary shadow-sm hover:bg-interactive-light hover:border-interactive-light active:bg-brand-blue-200 active:border-outline-interactive disabled:bg-white/50 disabled:border-white/50 disabled:text-white/50',
				lightSecondary:
					'bg-transparent border border-white/50 text-white hover:bg-interactive-light hover:border-outline-neutral hover:text-text-primary active:bg-brand-blue-200 active:border-outline-interactive active:text-text-primary disabled:border-white/50 disabled:text-white/50',

				// ── Dark theme (for light/colored backgrounds) ───────────────
				darkPrimary:
					'bg-zinc-900 border border-zinc-900 text-white shadow-sm hover:bg-zinc-700 hover:border-zinc-700 active:bg-zinc-800 active:border-zinc-800 disabled:bg-zinc-800 disabled:text-zinc-400 disabled:border-zinc-800',
				darkSecondary:
					'bg-transparent border border-zinc-800 text-zinc-800 hover:bg-zinc-700 hover:border-zinc-700 hover:text-white active:bg-zinc-800 active:border-zinc-800 active:text-white disabled:border-zinc-800/40 disabled:text-zinc-800/40',

				// ── Link ─────────────────────────────────────────────────────
				link: 'bg-transparent border-0 text-text-link underline-offset-4 hover:bg-interactive-light active:bg-brand-blue-200 disabled:text-zinc-400',

				// ── Neutral (minimal) ────────────────────────────────────────
				neutral:
					'bg-transparent border border-transparent text-text-secondary disabled:opacity-40 disabled:cursor-not-allowed',
			},
			size: {
				/** XS: 24px height, padding 4px 8px, gap 6px, radius 4px, icon 14px */
				'xs': [
					'h-24 px-8 py-4',
					'gap-6',
					'rounded-xs',
					'text-12 leading-lh6',
					"[&_svg:not([class*='size-'])]:size-14",
				].join(' '),

				/** S: 32px height, padding 8px 12px, gap 8px, radius 6px, icon 16px */
				's': [
					'h-32 px-12 py-8',
					'gap-8',
					'rounded-s',
					'text-12 leading-lh7',
					"[&_svg:not([class*='size-'])]:size-16",
				].join(' '),

				/** M: 36px height, padding 8px 12px, gap 8px, radius 8px, icon 16px */
				'm': [
					'h-36 px-12 py-8',
					'gap-8',
					'rounded-m',
					'text-14 leading-lh5',
					"[&_svg:not([class*='size-'])]:size-16",
				].join(' '),

				/** Icon XS: 22×22, padding 4px, radius 4px, icon 14px */
				'icon-xs': [
					'size-22 p-4',
					'rounded-xs',
					"[&_svg:not([class*='size-'])]:size-14",
				].join(' '),

				/** Icon S: 32×32, padding 8px, radius 6px, icon 16px */
				'icon-s': [
					'size-32 p-8',
					'rounded-s',
					"[&_svg:not([class*='size-'])]:size-16",
				].join(' '),

				/** Icon M: 36×36, padding 10px, radius 8px, icon 16px */
				'icon-m': [
					'size-36 p-10',
					'rounded-m',
					"[&_svg:not([class*='size-'])]:size-16",
				].join(' '),
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'xs',
		},
	}
);

type ButtonState = 'idle' | 'loading' | 'success' | 'error' | 'pending';

const LOADING_STATES: ButtonState[] = ['loading', 'pending'];

type ButtonVariant = NonNullable<
	VariantProps<typeof buttonVariants>['variant']
>;

type IButtonFiltersProps = Omit<ComponentProps<'button'>, 'children'> &
	VariantProps<typeof buttonVariants> & {
		'variant': 'filters';
		'applied': number;
		'onClear': () => void;
		'children'?: ReactNode;
		'asChild'?: false;
		'state'?: ButtonState;
		/**
		 * Optional `data-testid`. When provided it identifies the rendered
		 * button (and, for the `filters` variant, derives `-main`/`-applied`/
		 * `-clear` ids for the sub-parts). When omitted no test id is
		 * emitted, so multiple buttons no longer collide on a shared id.
		 */
		'data-testid'?: string;
	};

type IButtonStandardProps = ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		'variant'?: Exclude<ButtonVariant, 'filters'>;
		'applied'?: never;
		'onClear'?: never;
		'asChild'?: boolean;
		'state'?: ButtonState;
		/**
		 * Optional `data-testid`. When provided it is applied to the rendered
		 * button; when omitted no test id is emitted, so multiple buttons no
		 * longer collide on a shared hard-coded id.
		 */
		'data-testid'?: string;
	};

type IButtonProps = IButtonFiltersProps | IButtonStandardProps;

const ICON_SIZE_FOR_BUTTON_SIZE: Record<
	NonNullable<IButtonProps['size']>,
	IconSize
> = {
	'xs': 'xs',
	's': 's',
	'm': 's',
	'icon-xs': 'xs',
	'icon-s': 's',
	'icon-m': 's',
};

const FILTERS_ZONE_STRETCH_CLASS: Record<
	NonNullable<IButtonProps['size']>,
	{ main: string; clear: string }
> = {
	'xs': { main: '-ml-8', clear: '-my-4 -mr-8' },
	's': { main: '-ml-12', clear: '-my-8 -mr-12' },
	'm': { main: '-ml-12', clear: '-my-8 -mr-12' },
	'icon-xs': { main: '-ml-4', clear: '-my-4 -mr-4' },
	'icon-s': { main: '-ml-8', clear: '-my-8 -mr-8' },
	'icon-m': { main: '-ml-10', clear: '-my-10 -mr-10' },
};

function Button(props: IButtonProps) {
	const {
		className,
		variant,
		size,
		asChild = false,
		state,
		children,
		onClick,
		applied,
		onClear,
		disabled,
		type = 'button',
		'data-testid': dataTestid,
		...rest
	} = props;

	const isLoading = state !== undefined && LOADING_STATES.includes(state);
	const isDisabled = Boolean(disabled) || isLoading;

	if (variant === 'filters' && applied > 0) {
		const buttonSize = size ?? 'm';
		const filtersClassName = cn(
			buttonVariants({ variant, size, className }),
			isLoading && 'animate-pulse-hard'
		);
		const iconSize = ICON_SIZE_FOR_BUTTON_SIZE[buttonSize];
		const zoneStretchClass = FILTERS_ZONE_STRETCH_CLASS[buttonSize];

		return (
			<div
				data-slot="button"
				className={cn(filtersClassName, 'items-stretch gap-0 overflow-hidden')}
				data-testid={dataTestid}
			>
				<button
					type={type}
					disabled={isDisabled}
					onClick={onClick}
					className={cn(
						'inline-flex h-full flex-1 cursor-pointer items-center gap-8 border-0 bg-transparent px-12 font-[inherit] text-[inherit] disabled:cursor-not-allowed disabled:pointer-events-none',
						zoneStretchClass.main
					)}
					{...rest}
					data-testid={dataTestid ? `${dataTestid}-main` : undefined}
				>
					<span
						className="inline-flex size-20 shrink-0 items-center justify-center rounded-max bg-brand-blue-200 text-12 font-medium leading-none text-text-primary"
						data-testid={dataTestid ? `${dataTestid}-applied` : undefined}
					>
						{applied}
					</span>
					{children}
				</button>
				<button
					type="button"
					disabled={isDisabled}
					onClick={onClear}
					aria-label="Clear filters"
					className={cn(
						'inline-flex w-36 shrink-0 cursor-pointer items-center justify-center self-stretch border-0 border-l border-l-zinc-200 bg-transparent disabled:cursor-not-allowed disabled:pointer-events-none',
						zoneStretchClass.clear
					)}
					data-testid={dataTestid ? `${dataTestid}-clear` : undefined}
				>
					<Icon name="x" size={iconSize} />
				</button>
			</div>
		);
	}

	const ButtonComponent = asChild ? Slot : 'button';

	return (
		<ButtonComponent
			data-slot="button"
			className={cn(
				buttonVariants({ variant, size, className }),
				isLoading && 'animate-pulse-hard'
			)}
			type={type}
			onClick={onClick}
			disabled={isDisabled}
			{...rest}
			data-testid={dataTestid}
		>
			{children}
		</ButtonComponent>
	);
}

export type ButtonSize = NonNullable<
	VariantProps<typeof buttonVariants>['size']
>;
export { Button, buttonVariants };
export type { ButtonState, ButtonVariant, IButtonProps };
